/**
 * Browser Notification API helpers (OS toast while the app / browser is open).
 * Delivery still comes from WebSockets — this only surfaces the popup.
 */

const DEFAULT_ICON = "/flexhr.svg";
const DEFAULT_BADGE = "/flexhr.svg";

export function isBrowserNotificationSupported() {
  return typeof window !== "undefined" && "Notification" in window;
}

export function getBrowserNotificationPermission() {
  if (!isBrowserNotificationSupported()) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

export function stripNotificationHtml(html) {
  if (!html) return "";
  const asText = String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  return asText;
}

/**
 * Request permission. Must be called from a user gesture for best browser support.
 * @returns {Promise<"granted"|"denied"|"default"|"unsupported">}
 */
export async function requestBrowserNotificationPermission() {
  if (!isBrowserNotificationSupported()) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  try {
    const result = await Notification.requestPermission();
    return result;
  } catch (_e) {
    return getBrowserNotificationPermission();
  }
}

/**
 * Show a highly visible OS notification from a WS payload / notif body.
 * No-ops when permission is not granted.
 */
export function showBrowserNotification(input = {}) {
  if (!isBrowserNotificationSupported()) return null;
  if (Notification.permission !== "granted") return null;

  const title = String(
    input.title || input.notificationTitle || "FlexHR — Njoftim"
  ).trim();
  const rawBody =
    input.message ||
    input.body ||
    input.notificationBody ||
    input.text ||
    "";
  const body = stripNotificationHtml(rawBody) || "Keni një njoftim të ri.";
  const url = input.url || input.notificationUrl || "/kryefaqja";
  const tag =
    input.tag ||
    input.notificationId ||
    input.entityId ||
    `flexhr-${Date.now()}`;

  try {
    const n = new Notification(title, {
      body,
      icon: input.icon || DEFAULT_ICON,
      badge: input.badge || DEFAULT_BADGE,
      tag: String(tag),
      renotify: true,
      requireInteraction: true,
      silent: false,
      data: { url, ...(input.data || {}) },
    });

    n.onclick = () => {
      try {
        window.focus();
        const path = n.data?.url || url;
        if (path && typeof path === "string") {
          const target = path.startsWith("http")
            ? path
            : `${window.location.origin}${path.startsWith("/") ? "" : "/"}${path}`;
          window.location.assign(target);
        }
      } catch (_e) {
        // ignore
      }
      n.close();
    };

    // Auto-close after 20s if the OS keeps it open (requireInteraction may override)
    setTimeout(() => {
      try {
        n.close();
      } catch (_e) {
        // ignore
      }
    }, 20000);

    return n;
  } catch (err) {
    console.warn("showBrowserNotification failed", err);
    return null;
  }
}

/**
 * Snapshot of "subscription-like" browser push readiness (Notification API).
 */
export function checkBrowserPushSubscription() {
  const supported = isBrowserNotificationSupported();
  const permission = getBrowserNotificationPermission();
  return {
    supported,
    permission,
    subscribed: supported && permission === "granted",
    blocked: permission === "denied",
    pending: permission === "default",
  };
}

/**
 * Fire a visible test OS notification to verify the "subscription".
 * @returns {{ ok: boolean, reason?: string, notification?: Notification }}
 */
export function sendTestBrowserNotification() {
  const status = checkBrowserPushSubscription();
  if (!status.supported) {
    return { ok: false, reason: "unsupported" };
  }
  if (!status.subscribed) {
    return {
      ok: false,
      reason: status.blocked ? "denied" : "not_granted",
    };
  }

  const now = new Date();
  const time = now.toLocaleTimeString("sq-AL", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const notification = showBrowserNotification({
    title: "FlexHR — Njoftimi i testit",
    message: `Abonimi është aktiv. Ky është një njoftim prove (${time}).`,
    url: "/kryefaqja",
    tag: `flexhr-test-${now.getTime()}`,
    data: { test: true },
  });

  if (!notification) {
    return { ok: false, reason: "show_failed" };
  }
  return { ok: true, notification };
}
